<script>
export default {
  name: 'TermsOfService',
}
</script>

<template>
  <div class="terms-page-container">
    <div class="terms-container">
      <h1 class="title">Terms of Service</h1>

      <div class="terms-content">
        <p>Welcome to CrowdHRS ("we," "our," "us"). These Terms of Use ("Terms") govern your access and use of CrowdHRS.com ("Website"), operated by InspireDGT Ltd, a company registered in the United Kingdom. By using our services, you agree to comply with these Terms. If you do not agree, please refrain from using our platform.</p>

        <div class="terms-section">
          <h2>1. Eligibility & User Accounts</h2>
          <ul>
              <li>You must be at least 18 years old to use our services.</li>
              <li>You agree to provide accurate and up-to-date information when registering an account.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>2. Use of Services</h2>
          <ul>
              <li>CrowdHRS is a recruitment platform that connects job seekers, companies, and recruiters.</li>
              <li>Users must not engage in any illegal, fraudulent, or unethical activities while using the platform.</li>
              <li>You must not misuse our platform for spam, unauthorized advertisements, or misleading job postings.</li>
              <li>We reserve the right to remove content or terminate accounts that violate our policies.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>3. Job Posting & Applications</h2>
          <ul>
              <li>Companies and recruiters must provide accurate job descriptions and ensure compliance with applicable employment laws.</li>
              <li>Applicants must provide truthful information in their job applications and CVs.</li>
              <li>Referral rewards are subject to verification and the successful hiring of referred candidates.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>4. Payments & Fees</h2>
          <ul>
              <li>Certain features of CrowdHRS may require payments, including job postings and referral rewards.</li>
              <li>Payments are processed through third-party providers such as Stripe and PayPal.</li>
              <li>Fees paid for job postings are non-refundable unless otherwise stated.</li>
              <li>In the case of referral rewards, payouts will only be processed once the job poster confirms a successful hire.</li>
              <li>CrowdHRS reserves the right to charge a commission fee for facilitating referrals.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>5. User Conduct</h2>
          <ul>
              <li>You must not engage in harassment, hate speech, discrimination, or any harmful behavior.</li>
              <li>You must not attempt to hack, disrupt, or manipulate the Website or its services.</li>
              <li>You agree not to impersonate another person or misrepresent your affiliation with any entity.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>6. Intellectual Property</h2>
          <ul>
              <li>All content on CrowdHRS, including logos, text, graphics, and software, is the property of InspireDGT Ltd or its licensors.</li>
              <li>Users may not copy, distribute, or modify any content without explicit permission.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>7. Privacy & Data Protection</h2>
          <p>Our use of personal data is governed by our <router-link to="/privacy-policy" class="link">Privacy Policy</router-link>.</p>
        </div>

        <div class="terms-section">
          <h2>8. Limitation of Liability</h2>
          <ul>
              <li>CrowdHRS does not guarantee job placements or successful referrals.</li>
              <li>We are not responsible for any disputes between users, including recruiters, employers, and applicants.</li>
              <li>To the maximum extent permitted by law, InspireDGT Ltd shall not be liable for any indirect, incidental, or consequential damages arising from your use of our platform.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>9. Termination & Suspension</h2>
          <ul>
              <li>We may suspend or terminate your account at any time if you violate these Terms.</li>
              <li>Users may request account deletion by contacting <a href="mailto:info@inspiredgt.co.uk" class="link">info@inspiredgt.co.uk</a>.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>10. Changes to Terms</h2>
          <ul>
              <li>We reserve the right to update these Terms at any time. The latest version will always be available on CrowdHRS.com.</li>
              <li>Continued use of the platform after any changes constitutes acceptance of the revised Terms.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>11. Governing Law & Dispute Resolution</h2>
          <ul>
              <li>These Terms are governed by the laws of England and Wales.</li>
              <li>Any disputes shall be resolved in the courts of England and Wales.</li>
          </ul>
        </div>

        <div class="terms-section">
          <h2>12. Contact Information</h2>
          <div class="contact-info">
            <p><strong>InspireDGT Ltd</strong></p>
            <p>338a Regents Park Road, Office 3 And 4,<br>
            London, England, N3 2LN</p>
            <p>Email: <a href="mailto:info@inspiredgt.co.uk" class="link">info@inspiredgt.co.uk</a></p>
            <p>Website: <a href="http://www.CrowdHRS.com" class="link">www.CrowdHRS.com</a></p>
          </div>
        </div>

        <div class="terms-agreement">
          <p>By using CrowdHRS, you acknowledge that you have read, understood, and agreed to these Terms of Use.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Variables
$primary-color: #103660;
$accent-color: #fc6c2a;
$text-color: #333;
$light-gray: #f5f7fa;
$border-color: #eaeaea;
$link-color: #015cac;

.terms-page-container {
  padding: 2rem 1rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem 0.75rem;
  }
}

.terms-container {
  max-width: 1000px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  padding: 3rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 8px;
  }
}

.title {
  color: $primary-color;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
}

.last-updated {
  color: #666;
  font-style: italic;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 0.9rem;
}

.terms-content {
  > p {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    color: $text-color;
  }
}

.terms-section {
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid $border-color;
  
  &:last-child {
    border-bottom: none;
  }

  h2 {
    color: $primary-color;
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.25rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid $accent-color;
    display: inline-block;
  }

  p {
    margin-bottom: 1rem;
    line-height: 1.6;
    color: $text-color;
  }

  ul {
    padding-left: 1.5rem;
    margin-bottom: 1rem;

    li {
      margin-bottom: 0.75rem;
      line-height: 1.6;
      color: $text-color;
      position: relative;
      
      &::marker {
        color: $accent-color;
      }
    }
  }
}

.contact-info {
  background-color: $light-gray;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  
  p {
    margin-bottom: 0.75rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.terms-agreement {
  margin-top: 3rem;
  padding: 1.5rem;
  background-color: rgba($primary-color, 0.05);
  border-radius: 8px;
  text-align: center;
  
  p {
    font-weight: 600;
    color: $primary-color;
    font-size: 1.1rem;
  }
}

.link {
  color: $link-color;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: $accent-color;
    text-decoration: underline;
  }
}
</style> 